
import React, { useEffect } from 'react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-slate-100">
          <div>
            <h3 className="text-2xl font-black text-slate-900">Terms of Service</h3>
            <p className="text-slate-500 text-sm font-bold mt-1">Effective Date: 1 January 2026</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-6 md:p-8 space-y-8 text-slate-600 leading-relaxed text-sm md:text-base">
          <p className="font-medium">
            These Terms of Service ("Terms") govern your access to and use of services provided by Lookapp.Au, an independently operated web application service.
            By using our services, you agree to these Terms. If you do not agree, please do not use our services.
          </p>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">1. Services</h4>
            <p className="mb-3">Lookapp.Au provides custom web application development, hosting, and maintenance services including, but not limited to:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Web applications for events, blogs, or small business tools</li>
              <li>Ongoing maintenance and technical support</li>
              <li>Hosting and updates of the web application</li>
            </ul>
            <p className="mt-3">Services are provided as a sole operator and may include optional add-ons or custom features at additional cost.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">2. Payment Terms</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>All services require a one-time setup fee, invoiced before project commencement.</li>
              <li>Monthly services (hosting, maintenance, or subscription features) are billed in advance and payable on the agreed schedule.</li>
              <li>Late payments may result in temporary suspension of services until payment is received.</li>
              <li>Prices are subject to change with prior notice.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">3. User Responsibilities</h4>
            <p className="mb-3">Clients agree to:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Provide accurate and complete information required for project delivery</li>
              <li>Respond promptly to requests for content, feedback, or approvals</li>
              <li>Maintain credentials and accounts securely</li>
            </ul>
            <p className="mt-3">Clients are responsible for their own content and compliance with applicable laws.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">4. Intellectual Property</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>You retain ownership of your content and data uploaded to the platform.</li>
              <li>Lookapp.Au retains ownership of all source code, templates, and underlying software developed for the web application. Upon full payment of all agreed fees, including development costs, ownership or usage rights may be transferred as mutually agreed in writing.</li>
              <li>You are granted a non-exclusive license to use the software for your business purposes once the setup fee full payment has been made.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">5. Maintenance & Support</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Ongoing maintenance includes security updates, bug fixes, and minor enhancements, as outlined in your service plan.</li>
              <li>Lookapp.Au is not liable for downtime caused by external hosting providers, third-party plugins, or client-provided content.</li>
              <li>Support requests will be responded to in a reasonable timeframe. Priority response may require a higher-tier plan.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">6. Warranties & Disclaimers</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Lookapp.Au strives to provide reliable services, but cannot guarantee uninterrupted access or error-free operation.</li>
              <li>Services are provided "as-is", with no other warranties expressed or implied.</li>
              <li>Lookapp.Au is not responsible for any indirect, incidental, or consequential damages arising from use of the services.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">7. Termination</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Either party may terminate services with 30 days' notice after the initial term (minimum 6 as agreed).</li>
              <li>Upon termination, monthly services will cease, and any outstanding payments remain due.</li>
              <li>Lookapp.Au may terminate services immediately if the client breaches these Terms.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">8. Limitation of Liability</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Lookapp.Au is not liable for any loss of revenue, profits, or data.</li>
              <li>Maximum liability is limited to the amount paid by the client for services in the preceding 6 months.</li>
              <li>Clients are responsible for backups of their content and data.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">9. Governing Law</h4>
            <p>These Terms are governed by the law of the jurisdiction in which Lookapp.Au operates. Any disputes will be resolved in good faith or through alternative dispute resolution before legal action.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">10. Changes to Terms</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Lookapp.Au may update these Terms from time to time.</li>
              <li>Updated terms will be posted on the website and take effect immediately unless otherwise noted.</li>
              <li>Continued use of services after changes constitutes acceptance of the revised Terms.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">11. Contact Information</h4>
            <p>For any questions about these Terms or your services:</p>
            <p className="font-bold mt-2">Email: lookapp.au@gmail.com</p>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="p-6 md:p-8 border-t border-slate-100 bg-slate-50 rounded-b-3xl flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
